import { useState } from "react"
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
  type SortingState,
} from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Search,
  Download,
  Filter,
  FileText,
  Image,
  FileSpreadsheet,
  Mail,
  Trash2,
} from "lucide-react"
import { DeleteCustomerModal } from "@/components/DeleteCustomerModal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { TableDateDisplay } from "@/components/ui/date-display"

interface Customer {
  id: string
  name: string
  email: string
  address: string
  web: boolean
  ios: boolean
  android: boolean
  devices: number
  lastBill: string
  status: "active" | "inactive" | "suspended"
  joinDate: string
}

interface CustomersTableProps {
  customers: Customer[]
  compact?: boolean
  onCustomerClick?: (customerId: string) => void
  showRegistrationDate?: boolean
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "default"
    case "inactive":
      return "secondary"
    case "suspended":
      return "destructive"
    default:
      return "secondary"
  }
}

const columnHelper = createColumnHelper<Customer>()

export function CustomersTable({
  customers,
  compact = false,
  onCustomerClick,
  showRegistrationDate = false,
}: CustomersTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null)

  const exportData = (format: string) => {
    let content, mimeType, filename

    switch (format) {
      case "csv": {
        const headers = "ID,Name,Email,Phone,Status,Devices,Join Date,Last Bill"
        const rows = customers
          .map(
            (customer) =>
              `${customer.id},${customer.name},${customer.email},${customer.status},${customer.devices},${customer.joinDate},${customer.lastBill}`,
          )
          .join("\n")
        content = headers + "\n" + rows
        mimeType = "text/csv;charset=utf-8;"
        filename = "customers-export.csv"
        break
      }

      case "pdf": {
        content = `%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj

2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj

3 0 obj
<<
/Type /Page
/Parent 2 0 R
/MediaBox [0 0 612 792]
/Contents 4 0 R
>>
endobj

4 0 obj
<<
/Length 58
>>
stream
BT
/F1 12 Tf
100 700 Td
(Customers Export - ${customers.length} customers) Tj
ET
endstream
endobj

trailer
<<
/Size 5
/Root 1 0 R
>>
startxref
0
%%EOF`
        mimeType = "application/pdf"
        filename = "customers-export.pdf"
        break
      }

      case "png": {
        // Simple 1x1 transparent PNG
        content =
          "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
        mimeType = "image/png"
        filename = "customers-export.png"

        const link = document.createElement("a")
        link.href = `data:${mimeType};base64,${content}`
        link.download = filename
        link.click()
        return
      }

      default:
        content = JSON.stringify(customers, null, 2)
        mimeType = "application/json;charset=utf-8;"
        filename = "customers-export.json"
    }

    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
  }

  const columns = [
    columnHelper.accessor("name", {
      header: "Customer",
      cell: (info) => (
        <div>
          <p className="font-medium">{info.getValue()}</p>
          <div className="flex items-center gap-2 mt-1">
            <Mail className="size-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{info.row.original.email}</span>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => (
        <Badge variant={getStatusColor(info.getValue())} className="capitalize">
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor("devices", {
      header: "Devices",
      cell: (info) => <Badge variant="outline">{info.getValue()}</Badge>,
    }),
    columnHelper.display({
      id: "platforms",
      header: "Platforms",
      cell: ({ row }) => (
        <div className="flex gap-1">
          {row.original.web && (
            <Badge variant="secondary" className="text-xs">
              Web
            </Badge>
          )}
          {row.original.ios && (
            <Badge variant="secondary" className="text-xs">
              iOS
            </Badge>
          )}
          {row.original.android && (
            <Badge variant="secondary" className="text-xs">
              Android
            </Badge>
          )}
        </div>
      ),
    }),
    ...(showRegistrationDate && compact
      ? [
          columnHelper.accessor("joinDate", {
            header: "Registered",
            cell: (info) => (
              <span className="text-sm text-muted-foreground">
                <TableDateDisplay date={info.getValue()} />
              </span>
            ),
          }),
        ]
      : []),
    ...(compact
      ? []
      : [
          columnHelper.accessor("lastBill", {
            header: "Last Bill",
            cell: (info) => (
              <span className="text-sm text-muted-foreground">{info.getValue()}</span>
            ),
          }),
          columnHelper.display({
            id: "actions",
            header: "Actions",
            cell: ({ row }) => (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onCustomerClick?.(row.original.id)}
                  className="h-8"
                >
                  Open
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCustomerToDelete(row.original)
                    setDeleteModalOpen(true)
                  }}
                  className="size-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ),
          }),
        ]),
  ]

  const filteredData = customers.filter((customer) => {
    if (statusFilter === "active") return customer.status === "active"
    if (statusFilter === "inactive") return customer.status === "inactive"
    if (statusFilter === "suspended") return customer.status === "suspended"
    return true
  })

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageSize: compact ? 5 : 10,
      },
    },
  })

  if (compact) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="text-xs">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="text-xs">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Customer Management</CardTitle>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Download className="size-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => exportData("csv")}>
                  <FileSpreadsheet className="size-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportData("pdf")}>
                  <FileText className="size-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportData("png")}>
                  <Image className="size-4 mr-2" />
                  Export as PNG
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                placeholder="Search customers..."
                value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
                onChange={(event) => table.getColumn("name")?.setFilterValue(event.target.value)}
                className="pl-8"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Filter className="size-4" />
                  Status: {statusFilter === "all" ? "All" : statusFilter}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setStatusFilter("all")}>
                  All Customers
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("active")}>
                  Active
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("inactive")}>
                  Inactive
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("suspended")}>
                  Suspended
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-x-2 py-4">
            <div className="text-sm text-muted-foreground">
              Showing {table.getRowModel().rows.length} of {filteredData.length} customers
            </div>
            <div className="flex gap-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      <DeleteCustomerModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        customerName={customerToDelete?.name || ""}
        onConfirm={() => {
          setDeleteModalOpen(false)
          setCustomerToDelete(null)
        }}
      />
    </Card>
  )
}
